import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Check, Crown, Gift, ShieldCheck, ExternalLink, RefreshCw } from "lucide-react";

const SHOPIER_LINK = "https://www.shopier.com/noktaders/50725616";

const PERKS = [
  "Sınırsız yapay zeka konu anlatımı",
  "Sınırsız soru-cevap ve sesli sohbet",
  "Tüm deneme sınavlarına erişim",
  "Yanlış soru çözümleri ve rehberlik",
  "İlerleme takibi ve rozetler",
];

export default function Subscribe() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [trial, setTrial] = useState(null);
  const [trialBusy, setTrialBusy] = useState(false);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    api.get("/trial/status").then(({ data }) => setTrial(data.status)).catch(() => {});
  }, []);

  const requestTrial = async () => {
    setTrialBusy(true);
    try {
      await api.post("/trial/request");
      setTrial("pending");
      toast.success("Deneme talebin yöneticiye iletildi! Onaylanınca haber vereceğiz 🎁");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Talep gönderilemedi");
    } finally {
      setTrialBusy(false);
    }
  };

  const goToPayment = () => {
    setPaid(true);
    window.open(SHOPIER_LINK, "_blank", "noopener,noreferrer");
  };

  const checkPayment = async () => {
    setBusy(true);
    try {
      const u = await refresh();
      if (u?.is_subscribed) toast.success("Ödemen onaylandı, Premium aktif! 🎉");
      else toast.info("Ödemen henüz görünmüyor. Ödeme sonrası birkaç dakika sürebilir.");
    } catch {
      toast.error("Kontrol edilemedi");
    } finally {
      setBusy(false);
    }
  };

  if (user?.is_subscribed) {
    return (
      <div className="max-w-lg mx-auto text-center py-16">
        <div className="text-6xl mb-4">👑</div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Premium üyesin! 🎉</h1>
        <p className="text-slate-500 mt-2">Tüm özelliklerin kilidi açık. İyi çalışmalar!</p>
        {user.subscription_until && (
          <p className="text-sm text-slate-400 mt-3">Bitiş: {new Date(user.subscription_until).toLocaleDateString("tr-TR")}</p>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div className="text-center">
        <span className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1.5 rounded-full">
          <Crown className="w-4 h-4" /> PREMIUM
        </span>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mt-3">Başarıya bir nokta kaldı!</h1>
        <p className="text-slate-500 mt-2">Premium ile tüm derslerin ve sınavların kilidini aç.</p>
      </div>

      <div className="relative bg-gradient-to-br from-indigo-600 to-violet-600 rounded-3xl p-8 text-white shadow-2xl overflow-hidden" data-testid="premium-card">
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full" />
        <h3 className="font-display text-2xl font-bold">NoktaDers Premium</h3>
        <p className="mt-3 text-5xl font-extrabold">2.750 ₺<span className="text-lg font-medium text-indigo-200"> / ay</span></p>
        <p className="text-indigo-200 font-semibold text-sm">KDV Dahil</p>

        <ul className="mt-6 space-y-2.5">
          {PERKS.map((perk) => (
            <li key={perk} className="flex items-center gap-3 text-indigo-50">
              <span className="bg-white/20 rounded-full p-1"><Check className="w-4 h-4" /></span>
              <span className="text-sm">{perk}</span>
            </li>
          ))}
        </ul>

        <Button data-testid="pay-shopier-btn" onClick={goToPayment}
          className="w-full mt-7 bg-white text-indigo-700 hover:bg-indigo-50 rounded-xl font-bold py-6 text-base">
          Ödemeye Geç <ExternalLink className="w-4 h-4 ml-2" />
        </Button>
        {paid && (
          <Button data-testid="check-payment-btn" onClick={checkPayment} disabled={busy} variant="ghost"
            className="w-full mt-2 text-white hover:bg-white/10 rounded-xl font-semibold">
            <RefreshCw className={`w-4 h-4 mr-2 ${busy ? "animate-spin" : ""}`} /> Ödememi kontrol et
          </Button>
        )}
        <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-indigo-200">
          <ShieldCheck className="w-4 h-4" /> Ödeme Shopier güvenli altyapısı ile alınır · yeni sekmede açılır
        </p>
      </div>

      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-3xl p-6 text-center" data-testid="trial-card">
        <div className="text-4xl mb-2">🎁</div>
        <h3 className="font-display text-xl font-bold text-slate-900">Önce ücretsiz denemek ister misin?</h3>
        <p className="text-slate-500 mt-1 text-sm">7 günlük ücretsiz deneme talep et, yönetici onayladığında tüm özellikler açılsın.</p>
        {trial === "pending" ? (
          <div data-testid="trial-pending" className="mt-4 inline-flex items-center gap-2 bg-amber-100 text-amber-700 font-bold px-4 py-2.5 rounded-xl text-sm">
            ⏳ Talebin onay bekliyor...
          </div>
        ) : trial === "rejected" ? (
          <div data-testid="trial-rejected" className="mt-4 inline-flex items-center gap-2 bg-rose-100 text-rose-600 font-bold px-4 py-2.5 rounded-xl text-sm">
            Talebin bu sefer onaylanmadı. Premium ile devam edebilirsin.
          </div>
        ) : (
          <Button data-testid="request-trial-btn" onClick={requestTrial} disabled={trialBusy}
            className="mt-4 bg-emerald-600 hover:bg-emerald-700 rounded-xl font-bold px-6 py-6">
            <Gift className="w-4 h-4 mr-2" /> {trialBusy ? "Gönderiliyor..." : "7 Günlük Ücretsiz Deneme Talep Et"}
          </Button>
        )}
      </div>
    </div>
  );
}
